import { ActionIcon, Grid, Image, Modal, RemoveScroll, ScrollArea, Tooltip } from '@mantine/core';
import { Carousel } from '@mantine/carousel';
import { useTranslation } from 'react-i18next';
import { IOrder, IOrderEvidence } from '../../interfaces/Order';
import LoaderOverlay from '../../components/LoaderOverlay/LoaderOverlay';
import { memo, useState } from 'react';
import { IconTrash, IconX } from '@tabler/icons-react';
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal';
import { showNotification } from '../../utils/Notification/Notification';
import { useDeleteOrderEvidence } from '../../hooks/orders/mutations';
import useCanEditOrders from '../../hooks/useCanEditOrders';

export const ZoomImage = ({
  zoomSrc,
  setZoomSrc
}: {
  zoomSrc: string | null;
  setZoomSrc: (url: string | null) => void;
}) => {
  if (!zoomSrc) return null;
  return (
    <div
      onClick={() => setZoomSrc(null)}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999
      }}>
      <ActionIcon
        onClick={(e) => {
          e.stopPropagation();
          setZoomSrc(null);
        }}
        style={{ position: 'absolute', top: 16, right: 16 }}
        size="xl"
        variant="transparent"
        title="Cerrar">
        <IconX size={24} color="white" />
      </ActionIcon>

      <img
        src={zoomSrc}
        alt="Evidencia ampliada"
        style={{
          maxWidth: '90%',
          maxHeight: '90%',
          objectFit: 'contain',
          boxShadow: '0 0 20px rgba(0,0,0,0.7)'
        }}
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
};

export const EvidenceCarousel = ({
  evidence,
  setZoomSrc,
  onDeleteEvidence,
  isDeleting = false
}: {
  evidence: IOrder['evidence'];
  setZoomSrc: (url: string | null) => void;
  onDeleteEvidence?: (evidenceId: string) => void;
  isDeleting?: boolean;
}) => {
  const { t } = useTranslation(['CommonButtons']);
  const slides = (evidence || []).map((item: IOrderEvidence) => (
    <Carousel.Slide key={item._id || item.url}>
      <div
        style={{
          height: 400,
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          position: 'relative'
        }}>
        {onDeleteEvidence && item._id && (
          <Tooltip label={t('CommonButtons:delete')}>
            <ActionIcon
              aria-label={t('CommonButtons:delete')}
              color="red"
              data-testid={`delete-evidence-${item._id}`}
              disabled={isDeleting}
              onClick={(event) => {
                event.stopPropagation();
                onDeleteEvidence(item._id!);
              }}
              radius={0}
              size="lg"
              style={{ position: 'absolute', right: 12, top: 12, zIndex: 1 }}
              variant="filled"
            >
              <IconTrash size={18} />
            </ActionIcon>
          </Tooltip>
        )}
        <Image
          src={item.url}
          height={400}
          fit="contain"
          w="100%"
          loading="eager"
          style={{ cursor: 'pointer' }}
          onClick={() => setZoomSrc(item.url)}
        />
      </div>
    </Carousel.Slide>
  ));
  return (
    <Grid>
      <Grid.Col span={12} bg="lightgray">
        <Carousel key="carousel" slideSize={{ base: '100%' }} height={400} withIndicators>
          {slides}
        </Carousel>
      </Grid.Col>
    </Grid>
  );
};

export default memo(function OrderEvidencesCarouselModal({
  opened,
  close,
  title,
  order,
  onEvidenceDeleted
}: {
  opened: boolean;
  close: () => void;
  title: string;
  order: IOrder | null;
  onEvidenceDeleted?: (evidenceId: string) => void;
}) {
  const { t } = useTranslation(['OrdersPage', 'Notifications', 'CommonButtons']);
  const [zoomSrc, setZoomSrc] = useState<string | null>(null);
  const canEditOrders = useCanEditOrders();
  const deleteOrderEvidence = useDeleteOrderEvidence();

  const confirmDeleteEvidence = (evidenceId: string) => {
    if (!order?._id) return;

    ConfirmationModal({
      title: t('Notifications:CRUD.deleteConfirmation'),
      children: t('OrdersPage:evidenceModal.deleteConfirmation'),
      labels: {
        confirm: t('CommonButtons:delete'),
        cancel: t('CommonButtons:cancel')
      },
      confirmProps: {
        color: 'red'
      },
      onConfirm: async () => {
        try {
          await deleteOrderEvidence.mutateAsync({
            orderId: order._id!,
            evidenceId
          });
          onEvidenceDeleted?.(evidenceId);
          showNotification({ type: 'Default', title: t('Notifications:success') });
        } catch (e) {
          showNotification({ type: 'Error', title: t('Notifications:somethingWrong') });
        }
      }
    });
  };

  return (
    <>
      <Modal
        opened={opened}
        onClose={() => {
          setZoomSrc(null);
          close();
        }}
        className={RemoveScroll.classNames.fullWidth}
        title={title}
        size="xl"
        scrollAreaComponent={ScrollArea.Autosize}>
        {!order ? (
          <LoaderOverlay visible />
        ) : (
          <>
            <LoaderOverlay visible={deleteOrderEvidence.isPending} />
            <EvidenceCarousel
              evidence={order.evidence}
              setZoomSrc={setZoomSrc}
              onDeleteEvidence={canEditOrders && order._id ? confirmDeleteEvidence : undefined}
              isDeleting={deleteOrderEvidence.isPending}
            />
          </>
        )}
      </Modal>
      <ZoomImage zoomSrc={zoomSrc} setZoomSrc={setZoomSrc} />
    </>
  );
});
